import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import InfoIcon from '@src/svgs/InfoIcon';
import { SortChevronIcon } from '@src/svgs/SortChevronIcon';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { SortDir, SortKey } from '@src/stores-v2/StoryManagerViewStore';
import type { FC } from 'react';

type Props = {
  sortKey: SortKey | null;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  /** Whether the details panel is open (highlights the button). */
  detailsOpen: boolean;
  /** Toggle the details slide-out panel. */
  onToggleDetails: () => void;
};

const StoryListHeader: FC<Props> = ({
  sortKey,
  sortDir,
  onSort,
  detailsOpen,
  onToggleDetails,
}) => (
  <Row data-testid="story-list-header">
    <Left>
      <NameHeader
        type="button"
        onClick={() => onSort('name')}
        $active={sortKey === 'name'}
        data-testid="story-sort-name"
      >
        Story Name
        {sortKey === 'name' && <SortIcon $desc={sortDir === 'desc'} />}
      </NameHeader>
    </Left>
    <Right data-cols="true">
      <LengthHeader
        type="button"
        onClick={() => onSort('length')}
        $active={sortKey === 'length'}
        data-testid="story-sort-length"
      >
        Length
        {sortKey === 'length' && <SortIcon $desc={sortDir === 'desc'} />}
      </LengthHeader>
      <ContentHeader>Content</ContentHeader>
      <TopicsHeader>Topics</TopicsHeader>
      <LastUpdateHeader
        type="button"
        onClick={() => onSort('lastUpdate')}
        $active={sortKey === 'lastUpdate'}
        data-testid="story-sort-last-update"
      >
        Last activity
        {sortKey === 'lastUpdate' && <SortIcon $desc={sortDir === 'desc'} />}
      </LastUpdateHeader>
      <InfoButton
        type="button"
        onClick={onToggleDetails}
        $active={detailsOpen}
        aria-label="View details"
        data-tooltip-id="story-list-details-info"
        data-tooltip-content="View Details"
      >
        <InfoIcon />
      </InfoButton>
      <Tooltip
        id="story-list-details-info"
        style={COMPACT_TOOLTIP_STYLE}
        place="bottom"
        opacity={1}
      />
    </Right>
  </Row>
);

export default StoryListHeader;

const SortIcon: FC<{ $desc: boolean }> = ({ $desc }) => (
  <Caret aria-hidden="true" $desc={$desc}>
    <SortChevronIcon />
  </Caret>
);

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
  padding: var(--spacing-sm) var(--spacing-md);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  border-bottom: 1px solid var(--border);
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 1;
  min-width: 0;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex-shrink: 0;
`;

// Info toggle, aligned over the rows' kebab-menu column.
const InfoButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  flex-shrink: 0;
  padding: 4px 0;
  background: none;
  border: none;
  border-radius: var(--radius-button);
  color: ${({ $active }) =>
    $active ? 'var(--foreground)' : 'var(--text-muted)'};
  cursor: pointer;

  &:hover {
    color: var(--foreground);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const HeaderButton = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: ${({ $active }) =>
    $active ? 'var(--font-weight-medium)' : 'var(--font-weight-regular)'};
  color: ${({ $active }) =>
    $active ? 'var(--foreground)' : 'var(--text-muted)'};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: var(--foreground);
  }
`;

const NameHeader = styled(HeaderButton)`
  text-align: left;
`;

const LengthHeader = styled(HeaderButton)`
  width: 72px;
  justify-content: flex-start;
`;

// Non-sortable; mirrors the 180px Content cell.
const ContentHeader = styled.span`
  width: 180px;
  color: var(--text-muted);
`;

// Non-sortable label; mirrors UnfolderedStoryRow's 215px TopicsCol.
const TopicsHeader = styled.span`
  width: 215px;
  color: var(--text-muted);
`;

const LastUpdateHeader = styled(HeaderButton)`
  width: 88px;
  white-space: nowrap;
  justify-content: flex-end;
`;

const Caret = styled.span<{ $desc: boolean }>`
  display: inline-flex;
  transform: ${({ $desc }) => ($desc ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 120ms ease;
`;
